'use client';

import Link from 'next/link';
import { Suspense, useState, useEffect } from 'react';
import { useSettings, useWishlist } from '@/themes/core/hooks';
import MobileMenu from '../navbar/MobileMenu';
import SearchBar, { SearchSkeleton } from '../navbar/Search';
import CartModal from '../cart/CartModal';
import { User, ChevronDown, Heart, Search as SearchIcon } from 'lucide-react';
import { Menu } from '@/types/navigation';
import { AnnouncementBar } from './AnnouncementBar';
import { getStoreMenusByIds } from '@/actions/navigation';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
} from './Navigation';
import { Sheet, SheetTrigger, SheetContent, SheetTitle } from '@/themes/core/ui/sheet';
import { CurrencySelector } from './CurrencySelector';

interface StandardHeaderProps {
    // Configurable Props
    showAnnouncement?: boolean;
    announcementText?: string;
    stickyNavbar?: boolean;
    stickyAnnouncement?: boolean;
    transparent?: boolean;
    showSearch?: boolean;
    searchPlaceholder?: string;
    enableCustomerAccounts?: boolean;
    showCart?: boolean;
    showWishlist?: boolean;
    showCloseButton?: boolean;
    announcement_direction?: "auto" | "rtl" | "ltr";
    announcement_icon?: string;
    announcement_link_text?: string;
    announcement_link?: string;
    style?: string;
    banner03_text?: string;
    banner03_link?: string;
    banner03_icon?: string;
    banner03_direction?: "auto" | "rtl" | "ltr";

    // Global
    siteName?: string;
    logo?: string | null;
    menu?: { title: string; path: string }[];
    navbar_direction?: "auto" | "rtl" | "ltr";
    customMenus?: Menu[];
    basePath?: string;
}

export const StandardHeaderSchema = [
    {
        key: "showAnnouncement",
        label: "عرض شريط الإعلان",
        type: "boolean"
    },
    {
        key: "announcementText",
        label: "نص الإعلان",
        type: "text"
    },
    {
        key: "stickyAnnouncement",
        label: "تثبيت شريط الإعلان",
        type: "boolean"
    },
    {
        key: "showCloseButton",
        label: "إظهار زر الإغلاق",
        type: "boolean"
    },
    {
        key: "stickyNavbar",
        label: "تثبيت القائمة العلوية",
        type: "boolean"
    },
    {
        key: "transparent",
        label: "خلفية شفافة",
        type: "boolean"
    },
    {
        key: "showSearch",
        label: "عرض البحث",
        type: "boolean"
    },
    {
        key: "enableCustomerAccounts",
        label: "حسابات العملاء",
        type: "boolean"
    },
    {
        key: "showCart",
        label: "عرض السلة",
        type: "boolean"
    }
];

export const StandardHeaderDefaultProps = {
    showAnnouncement: true,
    announcementText: "🎉 شحن مجاني للطلبات فوق 500 ريال",
    stickyAnnouncement: false,
    stickyNavbar: false,
    transparent: false,
    showSearch: true,
    enableCustomerAccounts: true,
    showCart: true,
    _fixed: true // MARK AS FIXED/LOCKED
};

export function StandardHeader(props: StandardHeaderProps) {
    const settings = useSettings() as any;
    const { items: wishlistItems, openWishlist } = useWishlist();
    const wishlistCount = wishlistItems.length;

    // Merge props with settings (if needed) or just use props entirely for configurable stuff
    // For siteName/Logo, we fallback to settings if not passed props
    const siteName = props.siteName || settings?.siteName || "Store";
    const logo = props.logo || settings?.logo;
    const menu = props.menu || [];

    // Config values
    const showAnnouncement = props.showAnnouncement ?? true;
    const announcementText = props.announcementText || "🎉 شحن مجاني للطلبات فوق 500 ريال";
    const isTransparent = props.transparent ?? false;
    const stickyAnnouncement = props.stickyAnnouncement ?? false;
    const stickyNavbar = props.stickyNavbar ?? false;
    const stickWholeHeader = stickyAnnouncement && stickyNavbar;
    const navbar_direction = props.navbar_direction || 'auto';

    // Server Injected Menus
    const injectedMenus = props.customMenus || [];

    // Data Fetching for Menus
    const [customMenus, setCustomMenus] = useState<Menu[]>([]);

    useEffect(() => {
        const fetchMenus = async () => {
            const headerConfig = (settings.header as any) || {};
            let ids: string[] = [];

            if (Array.isArray(headerConfig.menus)) {
                ids = headerConfig.menus;
            } else {
                ids = [
                    headerConfig.menu_1_id,
                    headerConfig.menu_2_id,
                    headerConfig.menu_3_id,
                    headerConfig.menu_4_id
                ].filter(Boolean);
            }

            // 1. If we have server-injected menus, filter them to only those that should be in the header
            if (injectedMenus.length > 0) {
                const filtered = injectedMenus.filter(m => ids.includes(m.id) || ids.includes(m.handle));
                setCustomMenus(filtered);
                return;
            }

            // 2. Otherwise fetch them
            if (ids.length > 0) {
                try {
                    const fetched = await getStoreMenusByIds(ids);
                    // Sort fetched menus based on the order of IDs in the settings
                    const sorted = ids
                        .map(id => fetched.find(m => m.id === id || m.handle === id))
                        .filter((m): m is Menu => !!m);

                    setCustomMenus(sorted);
                } catch (e) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.warn("Failed to fetch menus", e);
                    }
                }
            } else {
                setCustomMenus([]);
            }
        };

        fetchMenus();
    }, [settings.header, injectedMenus]);

    // Resolve Menus
    // Simple Tree Builder
    function buildMenuTree(items: any[]): any[] {
        const map = new Map<string, any>();
        const roots: any[] = [];

        // 1. Create nodes
        items.forEach(item => {
            map.set(item.id, {
                ...item,
                title: item.title,
                path: item.url || '#', // Map url (from DB) to path
                children: [] // Initialize children
            });
        });

        // 2. Connect tree
        items.forEach(item => {
            const node = map.get(item.id)!;
            // Handle different property names for parent id if necessary
            const parentId = item.parent_id || item.parentId;

            if (parentId && map.has(parentId)) {
                map.get(parentId)!.children!.push(node);
            } else {
                roots.push(node);
            }
        });

        // 3. Sort by position
        return roots.map(root => {
            if (root.children.length > 0) {
                root.children.sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
            }
            return root;
        }).sort((a, b) => (a.position || 0) - (b.position || 0));
    }

    let displayItems: any[] = [];

    // Combine items from ALL selected menus - properly built as trees
    if (customMenus.length > 0) {
        // Build a single unified tree or list of trees
        let allItems: any[] = [];
        customMenus.forEach(menu => {
            if (menu.items) {
                allItems = [...allItems, ...menu.items];
            }
        });

        // Remove duplicates if any (based on ID) to be safe
        const uniqueItems = Array.from(new Map(allItems.map(item => [item.id, item])).values());
        displayItems = buildMenuTree(uniqueItems);
    } else {
        // Default to props.menus
        displayItems = menu;
    }

    const navMenu = displayItems;

    const resolvePath = (path: string) => {
        if (!path) return '#';
        if (path.startsWith('http')) return path;

        // Clean path
        const cleanPath = path.startsWith('/') ? path : `/${path}`;

        // Use passed basePath (e.g., /store/[slug])
        if (props.basePath) {
            // Avoid double slashes if basePath ends with /
            const normalizedBasePath = props.basePath.endsWith('/') ? props.basePath.slice(0, -1) : props.basePath;
            if (!cleanPath.startsWith(normalizedBasePath)) {
                return `${normalizedBasePath}${cleanPath}`;
            }
        }

        return cleanPath;
    };

    // Hover State for Dropdowns
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    let timeoutId: NodeJS.Timeout;

    const handleMouseEnter = (title: string) => {
        if (timeoutId) clearTimeout(timeoutId);
        setActiveMenu(title);
    };

    const handleMouseLeave = () => {
        timeoutId = setTimeout(() => {
            setActiveMenu(null);
        }, 100);
    };

    return (
        <div className={`relative z-40 ${stickWholeHeader ? 'sticky top-0 w-full' : ''}`}>
            {/* Announcement Bar */}
            <AnnouncementBar
                showAnnouncement={showAnnouncement}
                announcementText={announcementText}
                stickyAnnouncement={stickyAnnouncement}
                showCloseButton={props.showCloseButton}
                announcement_direction={props.announcement_direction}
                announcement_icon={props.announcement_icon}
                announcement_link_text={props.announcement_link_text}
                announcement_link={props.announcement_link}
                style={props.style}
                banner03_text={props.banner03_text}
                banner03_link={props.banner03_link}
                banner03_icon={props.banner03_icon}
                banner03_direction={props.banner03_direction}
            />

            <nav
                dir={navbar_direction === 'auto' ? undefined : navbar_direction}
                className={`w-full transition-colors duration-300 ${isTransparent
                    ? 'absolute left-0 right-0 z-40 bg-transparent text-white border-b border-white/10'
                    : `relative bg-white text-neutral-900 border-b border-neutral-200 dark:bg-black dark:text-white dark:border-neutral-800 ${!stickWholeHeader && stickyNavbar ? 'sticky top-0 z-40' : ''
                    }`
                    }`}>
                <div className="flex items-center justify-between p-4 lg:px-6">
                    <div className="block flex-none md:hidden">
                        <Suspense fallback={null}>
                            <MobileMenu
                                menu={navMenu.map(m => ({ ...m, path: resolvePath(m.path) }))}
                                direction={navbar_direction}
                                searchPlaceholder={props.searchPlaceholder}
                            />
                        </Suspense>
                    </div>

                    {/* Left Side: Logo + Desktop Menu */}
                    <div className="flex flex-1 items-center gap-4 lg:gap-6">
                        {/* Logo */}
                        <Link
                            href={resolvePath('/')}
                            prefetch={true}
                            className="flex items-center justify-center"
                        >
                            {logo ? (
                                <img src={logo} alt={siteName} className="h-8 w-auto object-contain" width={128} height={32} />
                            ) : (
                                <div className={`flex h-10 w-10 items-center justify-center rounded-lg border text-lg font-bold ${isTransparent
                                    ? 'border-white/20 bg-white/10 text-white'
                                    : 'border-neutral-200 bg-white text-black dark:bg-black dark:text-white dark:border-neutral-800'
                                    }`}>
                                    <span>{siteName.charAt(0)}</span>
                                </div>
                            )}
                            <div className={`mr-2 hidden text-sm font-medium uppercase md:block ${isTransparent ? 'text-white' : 'text-neutral-900 dark:text-white'
                                }`}>
                                {siteName}
                            </div>
                        </Link>

                        {/* Desktop Menu Links */}
                        {navMenu.length ? (
                            <div className="hidden md:block">
                                <NavigationMenu>
                                    <NavigationMenuList className="gap-2">
                                        {navMenu.map((item) => (
                                            <NavigationMenuItem
                                                key={item.title}
                                                onMouseEnter={() => handleMouseEnter(item.title)}
                                                onMouseLeave={handleMouseLeave}
                                                className="relative z-20"
                                            >
                                                {/* Check for children/subtitles */}
                                                {(item as any).children && (item as any).children.length > 0 ? (
                                                    <>
                                                        <Link
                                                            href={resolvePath(item.path)}
                                                            className={`inline-flex h-10 items-center justify-center px-4 py-2 text-xs font-medium transition-colors cursor-pointer group pointer-events-auto relative z-50 ${isTransparent ? 'text-white hover:text-white/80' : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white'} ${activeMenu === item.title ? "text-primary dark:text-primary" : ""}`}
                                                        >
                                                            {item.title}
                                                            <ChevronDown
                                                                className={`ml-1 h-3 w-3 transition-transform duration-200 group-hover:rotate-180 ${activeMenu === item.title ? 'rotate-180' : ''}`}
                                                            />
                                                        </Link>
                                                        <NavigationMenuContent isOpen={activeMenu === item.title} variant="standard">
                                                            <ul className="grid gap-1 min-w-[200px]">
                                                                {(item as any).children.map((child: any) => (
                                                                    <li key={child.title || child.id}>
                                                                        <Link
                                                                            href={resolvePath(child.path || child.url || '#')}
                                                                            className="block select-none pointer-events-auto space-y-1 rounded-md p-2.5 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                                        >
                                                                            <div className="text-sm font-medium leading-none">{child.title}</div>
                                                                        </Link>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </NavigationMenuContent>
                                                    </>
                                                ) : (
                                                    <Link
                                                        href={resolvePath(item.path)}
                                                        className={`inline-flex h-10 items-center justify-center px-4 py-2 text-xs font-medium transition-colors cursor-pointer group pointer-events-auto relative z-50 ${activeMenu === item.title ? "text-primary" : "text-muted-foreground hover:text-foreground"
                                                            }`}
                                                    >
                                                        {item.title}
                                                        {item.children && item.children.length > 0 && (
                                                            <ChevronDown
                                                                className={`ml-1 h-3 w-3 transition-transform duration-200 group-hover:rotate-180 ${activeMenu === item.title ? 'rotate-180' : ''}`}
                                                            />
                                                        )}
                                                    </Link>
                                                )}
                                            </NavigationMenuItem>
                                        ))}
                                    </NavigationMenuList>
                                </NavigationMenu>
                            </div>
                        ) : null}
                    </div>

                    {/* Center: Search */}
                    <div className="hidden flex-1 justify-center md:flex">
                        {(props.showSearch ?? true) && (
                            <div className="w-full max-w-sm">
                                <Suspense fallback={<SearchSkeleton />}>
                                    <SearchBar
                                        direction={navbar_direction}
                                        placeholder={props.searchPlaceholder}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Right: Cart and Wishlist */}
                    <div className="flex flex-1 justify-end items-center gap-2">
                        {/* User Account Button */}
                        {(props.enableCustomerAccounts ?? true) && (
                            <Link
                                href={resolvePath('/account')}
                                className={`relative p-2 rounded-lg transition-colors ${isTransparent
                                    ? 'text-white/80 hover:text-white hover:bg-white/10'
                                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-800'
                                    }`}
                                aria-label="الحساب"
                            >
                                <User className="h-5 w-5" />
                            </Link>
                        )}

                        {/* Currency Selector */}
                        <CurrencySelector variant={isTransparent ? 'transparent' : 'default'} />

                        {/* Search Button (Mobile) */}
                        <div className="block md:hidden">
                            <Sheet>
                                <SheetTrigger asChild>
                                    <button
                                        className={`relative p-2 rounded-lg transition-colors ${isTransparent
                                            ? 'text-white/80 hover:text-white hover:bg-white/10'
                                            : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-800'
                                            }`}
                                        aria-label="بحث"
                                    >
                                        <SearchIcon className="h-5 w-5" />
                                    </button>
                                </SheetTrigger>
                                <SheetContent
                                    side={navbar_direction === 'rtl' ? "right" : "left"}
                                    className={`w-[300px] sm:w-[400px] p-0 ${navbar_direction === 'rtl' ? '[&>button]:left-4 [&>button]:right-auto' : ''}`}
                                >
                                    <SheetTitle className="sr-only">البحث</SheetTitle>
                                    <div
                                        dir={navbar_direction === 'auto' ? undefined : (navbar_direction as any)}
                                        className="flex flex-col h-full bg-background"
                                    >
                                        <div className="p-4 border-b flex items-center justify-between">
                                            <span className="text-lg font-bold">{siteName}</span>
                                        </div>
                                        <div className="p-4 border-b">
                                            <Suspense fallback={<div className="h-10 w-full bg-muted animate-pulse rounded-md" />}>
                                                <SearchBar
                                                    direction={navbar_direction}
                                                    placeholder={props.searchPlaceholder}
                                                />
                                            </Suspense>
                                        </div>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>

                        {/* Wishlist Button */}
                        {(props.showWishlist ?? true) && (
                            <button
                                onClick={() => openWishlist()}
                                className={`relative p-2 rounded-lg transition-colors ${isTransparent
                                    ? 'text-white/80 hover:text-white hover:bg-white/10'
                                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                                    } ${!isTransparent && 'dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-800'}`}
                                aria-label="المفضلة"
                            >
                                <Heart className="h-5 w-5" />
                                {wishlistCount > 0 && (
                                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-secondary-foreground">
                                        {wishlistCount > 99 ? '99+' : wishlistCount}
                                    </span>
                                )}
                            </button>
                        )}

                        {(props.showCart ?? true) && (
                            <CartModal variant="minimal" />
                        )}
                    </div>
                </div>
            </nav >
        </div >
    );
}
